/**
 * A playbook describes one kind of negotiator: what they prepare, who they
 * rehearse against, what their report asks and which certificate it earns.
 *
 * It is plain, serialisable data on purpose — no functions — so the same shape
 * can later live in a database table and be authored in Studio. Everything a
 * learner reads is an i18n key resolved by `playbookCopy()`; model prompts are
 * English and get an output-language line, like the rest of the AI pipeline.
 */
export const PLAYBOOK_IDS = ["crisis-negotiation"] as const;

export type PlaybookId = (typeof PLAYBOOK_IDS)[number];

/** Keys into the playbook copy tables in `src/shared/i18n/playbooks.ts`. */
export type PlaybookCopyKey = string;

export type PrepFieldKind = "text" | "longtext";

export interface PrepField {
  id: string;
  label: PlaybookCopyKey;
  hint: PlaybookCopyKey;
  kind: PrepFieldKind;
  /** Prep counts as done once every required field has text. */
  required: boolean;
  /** Server-enforced character cap. */
  maxLength: number;
}

export interface Playbook {
  id: PlaybookId;
  /** `courses.framework` slug — links the playbook to its catalog framework. */
  framework: string;
  labels: {
    name: PlaybookCopyKey;
    summary: PlaybookCopyKey;
    /** What the counterpart is called inside this playbook ("Subject"). */
    counterpart: PlaybookCopyKey;
    learner: PlaybookCopyKey;
  };
  prep: PrepField[];
  /** Training-only playbooks never accept real incident material. */
  trainingOnly: boolean;
}

/** The four steps every playbook walks through, in order. */
export const LOOP_STAGES = ["prep", "rehearse", "report", "certified"] as const;

export type LoopStage = (typeof LOOP_STAGES)[number];
